import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { ShareAppartementInfoDto } from "src/models/estate/key-info.model";
import { KeyValuePairListComponent, KeyValuePair } from "../../components/key-value-pair-list/key-value-pair-list.component";
import { ConvertUtil } from "src/util/tools/convert.util";
import { INeighborhoodInfoModel } from "src/models/units/neighborhood.model";
import { toStringHour } from "src/app/shared/pipes/hour.pipe";

const AREAL_TOOLTIPS = {
    BRAi1: "Internt bruksareal. Bruksareal av boenheten innenfor omsluttende vegger.",
    BRAe1: "Eksternt bruksareal. Bruksareal av alle rom som ligger utenfor boenheten/boenhetene, men som tilhører denne/disse.",
    BRAb1: "Innglasset balkong mv. Bruksareal av innglasset balkong tilknyttet boenheten. I BRA-b inngår også innglasset veranda eller altan.",
    BRAi2: "Bruksareal av boenheten innenfor omsluttende vegger.",
    BRAe2: "Bruksareal av alle rom som ligger utenfor boenheten/boenhetene, men som tilhører denne/disse.",
    BRAb2: "Innglasset balkong mv. Bruksareal av innglasset balkong tilknyttet boenheten. I BRA-b inngår også innglasset veranda eller altan.",
    ALH: "Areal med lav himlingshøyde, under 1,90 meter.",
    GUA: "Summen av bruksareal (BRA) og areal med lav himlingshøyde (ALH).",
    TBA: "Areal av terrasser og åpne balkonger tilknyttet boenheten."
};

@Component({
    selector: "key-info-details",
    templateUrl: "./key-info-details.component.html",
    styleUrl: "./key-info-details.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [KeyValuePairListComponent],
})
export class KeyInfoDetailsComponent {
    readonly keyInfoData = input.required<ShareAppartementInfoDto>();
    readonly neighbourhoodData = input<INeighborhoodInfoModel>();

    protected readonly mainInfo = computed<KeyValuePair[]>(() => {
        const info = this.keyInfoData();
        if (!info) {
            return [];
        }
        return [
            //todo: Vise riktige data når keyinfo er fylt opp
            { key: "Byggeår", value: info.buildYear },
            { key: "Etasje", value: info.floorNumber },
            { key: "Bruksenhetsnummer", value: info.functionalUnitNumber },
            { key: "Etasjer", value: info.numberOfFloors },
            { key: "Antall rom", value: info.numberOfRooms }
        ];
    });

    protected readonly neighbourhoodInfo = computed<KeyValuePair[]>(() => {
        const neighbourhood = this.neighbourhoodData();
        if (!neighbourhood) {
            return [];
        }
        return [
            { key: "Offentlig transport", value: neighbourhood.transport ? neighbourhood.transport.toString() + " m" : "-" },
            { key: "Dagligvare", value: neighbourhood.grocery ? neighbourhood.grocery.toString() + " m" : "-" },
            { key: "Barnehager 1 km radius", value: neighbourhood.kindergarden ? neighbourhood.kindergarden.toString() : "-" },
            { key: "Barnefamilier i grunnkrets", value: neighbourhood.familiesWithKidsPercentage ? `${neighbourhood.familiesWithKidsPercentage}%` : "-" },
        ];
    });

    protected readonly areasInfo = computed<KeyValuePair[]>(() => {
        const info = this.keyInfoData();

        if (!info) {
            return [];
        }

        return [
            { key: 'BRA-i: Internt bruksareal', value: "-", tooltip: AREAL_TOOLTIPS.BRAi1 },
            { key: 'BRA-e: Eksternt bruksareal', value: "-", tooltip: AREAL_TOOLTIPS.BRAe1 },
            { key: 'BRA-b: Innglasset balkong', value: "-", tooltip: AREAL_TOOLTIPS.BRAb1 },
            { key: 'BRA', value: info.usableArea },
            { key: 'ALH: Areal med lav himlingshøyde', value: "-", tooltip: AREAL_TOOLTIPS.ALH },
            { key: 'GUA: Gulvareal', value: "-", tooltip: AREAL_TOOLTIPS.GUA },
            { key: 'TBA: Åpent areal', value: "-", tooltip: AREAL_TOOLTIPS.TBA },
            { key: 'P-ROM', value: "-" },
            { key: 'BTA', value: "-" },
        ];
    });

    protected readonly featuresInfo = computed<KeyValuePair[]>(() => {
        const features = this.keyInfoData()?.propertyFeatures;
        if (!features) {
            return [];
        }
        return [
            { key: "Meter over havet", value: features.altitude ? features.altitude.toString() + " m" : "-" },
            { key: "Avstand til vei", value: features.roadDistance ? features.roadDistance.toString() + " m" : "-" },
            { key: "Solnedgang", value: features.sunsetHour ? 'kl ' + toStringHour(features.sunsetHour) : "-" },
        ];
    });
}